import { Plus, Trash2 } from "lucide-react";
import type {
  UseFormWatch,
  UseFormSetValue,
} from "react-hook-form";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { ProjectDataInput } from "./schema";

/**
 * R5–R7 — additional investitori, co-authors (suradnici) and the
 * newly-formed-parcel (parcelacija) inputs.
 *
 * All optional: an empty ``investitori`` list, no suradnici and a null
 * parcelacija reproduce today's single-investitor / existing-parcel
 * document exactly (the backend collapses a single investitor to the
 * scalar fields). Rendered inside the Investitor section of
 * ``ProjectDataForm`` using the same form instance (watch/setValue), so
 * nothing about the existing scalar fields changes.
 */
export function PartiesFields({
  watch,
  setValue,
}: {
  watch: UseFormWatch<ProjectDataInput>;
  setValue: UseFormSetValue<ProjectDataInput>;
}) {
  const investitori = watch("investitori") ?? [];
  const suradnici = watch("suradnici") ?? [];
  const parcelacija = watch("parcelacija") ?? null;

  const dirty = () => ({ shouldDirty: true });

  return (
    <div className="mt-4 flex flex-col gap-5 border-t border-stone-100 pt-4">
      {/* R5 — additional investitori (the first investitor is the scalar
          fields above). */}
      <div>
        <div className="flex items-center justify-between">
          <Label>Dodatni investitori (opcionalno)</Label>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() =>
              setValue(
                "investitori",
                [...investitori, { name: "", address: "", oib: "" }],
                dirty(),
              )
            }
          >
            <Plus className="h-3.5 w-3.5" /> Dodaj investitora
          </Button>
        </div>
        {investitori.length === 0 ? (
          <p className="mt-1 text-[0.74rem] text-stone-400">
            Jedan investitor — polja gore. Dodaj još ako projekt ima više
            investitora (svi se raspisuju na svakoj izjavi).
          </p>
        ) : (
          <ul className="mt-2 flex flex-col gap-2">
            {investitori.map((inv, i) => (
              <li
                key={i}
                className="grid grid-cols-1 gap-2 rounded-md border border-stone-200 bg-stone-50/60 p-2.5 sm:grid-cols-[1fr_1fr_auto]"
              >
                <Input
                  placeholder="Naziv / ime"
                  value={inv.name ?? ""}
                  onChange={(e) => {
                    const next = [...investitori];
                    next[i] = { ...next[i], name: e.target.value };
                    setValue("investitori", next, dirty());
                  }}
                />
                <Input
                  placeholder="Adresa"
                  value={inv.address ?? ""}
                  onChange={(e) => {
                    const next = [...investitori];
                    next[i] = { ...next[i], address: e.target.value };
                    setValue("investitori", next, dirty());
                  }}
                />
                <div className="flex items-center gap-1.5">
                  <Input
                    placeholder="OIB"
                    value={inv.oib ?? ""}
                    onChange={(e) => {
                      const next = [...investitori];
                      next[i] = { ...next[i], oib: e.target.value };
                      setValue("investitori", next, dirty());
                    }}
                  />
                  <button
                    type="button"
                    title="Ukloni investitora"
                    className="rounded p-1.5 text-stone-400 hover:bg-red-50 hover:text-red-600"
                    onClick={() =>
                      setValue(
                        "investitori",
                        investitori.filter((_, j) => j !== i),
                        dirty(),
                      )
                    }
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* R6 — suradnici (ime + titula, no HKA). */}
      <div>
        <div className="flex items-center justify-between">
          <Label>Suradnici (opcionalno)</Label>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() =>
              setValue(
                "suradnici",
                [...suradnici, { ime: "", titula: "" }],
                dirty(),
              )
            }
          >
            <Plus className="h-3.5 w-3.5" /> Dodaj suradnika
          </Button>
        </div>
        {suradnici.length === 0 ? (
          <p className="mt-1 text-[0.74rem] text-stone-400">
            Raspisuju se uz glavnog projektanta (npr. „Ivan Brozina
            mag.ing.arch.").
          </p>
        ) : (
          <ul className="mt-2 flex flex-col gap-2">
            {suradnici.map((s, i) => (
              <li
                key={i}
                className="grid grid-cols-1 gap-2 rounded-md border border-stone-200 bg-stone-50/60 p-2.5 sm:grid-cols-[1fr_1fr_auto]"
              >
                <Input
                  placeholder="Ime i prezime"
                  value={s.ime ?? ""}
                  onChange={(e) => {
                    const next = [...suradnici];
                    next[i] = { ...next[i], ime: e.target.value };
                    setValue("suradnici", next, dirty());
                  }}
                />
                <Input
                  placeholder="Titula (npr. mag.ing.arch.)"
                  value={s.titula ?? ""}
                  onChange={(e) => {
                    const next = [...suradnici];
                    next[i] = { ...next[i], titula: e.target.value };
                    setValue("suradnici", next, dirty());
                  }}
                />
                <button
                  type="button"
                  title="Ukloni suradnika"
                  className="justify-self-start rounded p-1.5 text-stone-400 hover:bg-red-50 hover:text-red-600"
                  onClick={() =>
                    setValue(
                      "suradnici",
                      suradnici.filter((_, j) => j !== i),
                      dirty(),
                    )
                  }
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* R7 — parcelacija (newly-formed parcel). */}
      <div>
        <label className="flex items-center gap-2 text-[0.82rem] font-medium text-stone-700">
          <input
            type="checkbox"
            checked={parcelacija !== null}
            onChange={(e) =>
              setValue(
                "parcelacija",
                e.target.checked
                  ? { novi_broj: "", maticne: [], ko: "" }
                  : null,
                dirty(),
              )
            }
          />
          Novoformirana čestica (parcelacija)
        </label>
        {parcelacija !== null && (
          <div className="mt-2 grid grid-cols-1 gap-2 rounded-md border border-stone-200 bg-stone-50/60 p-2.5 sm:grid-cols-3">
            <div>
              <Label className="text-[0.72rem]">Novi broj k.č.</Label>
              <Input
                placeholder="npr. 706/1"
                value={parcelacija.novi_broj ?? ""}
                onChange={(e) =>
                  setValue(
                    "parcelacija",
                    { ...parcelacija, novi_broj: e.target.value },
                    dirty(),
                  )
                }
              />
            </div>
            <div>
              <Label className="text-[0.72rem]">Matične k.č. (zarezom)</Label>
              <Input
                placeholder="npr. 706, 707"
                value={(parcelacija.maticne ?? []).join(", ")}
                onChange={(e) =>
                  setValue(
                    "parcelacija",
                    {
                      ...parcelacija,
                      maticne: e.target.value
                        .split(",")
                        .map((x) => x.trim())
                        .filter(Boolean),
                    },
                    dirty(),
                  )
                }
              />
            </div>
            <div>
              <Label className="text-[0.72rem]">k.o.</Label>
              <Input
                placeholder="npr. Ičići"
                value={parcelacija.ko ?? ""}
                onChange={(e) =>
                  setValue(
                    "parcelacija",
                    { ...parcelacija, ko: e.target.value },
                    dirty(),
                  )
                }
              />
            </div>
            {parcelacija.novi_broj && (
              <p className="sm:col-span-3 text-[0.74rem] italic text-stone-500">
                „na novoformiranoj k.č. {parcelacija.novi_broj}
                {(parcelacija.maticne ?? []).length > 0 &&
                  ` nastaloj od dijela k.č. ${joinHr(parcelacija.maticne ?? [])}`}
                {parcelacija.ko && ` k.o. ${parcelacija.ko}`}"
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function joinHr(items: string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  return `${items.slice(0, -1).join(", ")} i ${items[items.length - 1]}`;
}
